// Competition scope for the stats widgets (C2) — the SUBJECT player's own data
// scope, at the top of the widget. A scope is one of three shapes, and it always
// resolves to a plain list of competition_ids handed to POST /player-stats
// (which since C1 accepts ≥1 and sums raw across them):
//
//   • all         → every competition the player played that season
//   • one <id>    → just that competition
//   • except <id> → every competition that season EXCEPT that one ("all except")
//
// Keep this axis SEPARATE from (B)'s compare-against population filter. Pure +
// DOM-free so it unit-tests without React; the widget keeps the chosen scope per
// column in its `state` (keyed by refKey, like `state.colors`).
//
// C2a wires only `all` + `one` (a plain picker). `except` is defined here now so
// the resolver is complete; C2c adds the select/exclude PILL UI that produces it.

export type CompetitionScope =
  | { mode: "all" }
  | { mode: "one"; competitionId: number }
  | { mode: "except"; competitionId: number };

/** Unique, order-preserving. */
function uniq(ids: number[]): number[] {
  return [...new Set(ids)];
}

/**
 * The label shown on the scope chip (Ben's `filter-chip`): "All competitions",
 * the competition's name for `one`, or "Not <name>" for `except`. Falls back to
 * a generic label when the competition isn't in the given list (e.g. it hasn't
 * loaded yet). Pure — unit-tested.
 */
export function competitionChipLabel(
  scope: CompetitionScope,
  competitions: { id: number; name: string }[]
): string {
  if (scope.mode === "all") return "All competitions";
  const name = competitions.find((c) => c.id === scope.competitionId)?.name;
  if (scope.mode === "one") return name ?? "1 competition";
  return name ? `Not ${name}` : "All except 1";
}

/**
 * Resolve a scope to the competition_ids to query, given the competitions the
 * player played THAT season. `one` returns its id even if it isn't in the season
 * list (the caller's anchor competition may resolve before the list loads);
 * `all`/`except` are derived from the list, so they yield [] until it's known —
 * the widget disables the stats query on an empty list.
 */
export function resolveCompetitionScope(
  scope: CompetitionScope,
  seasonCompetitionIds: number[]
): number[] {
  switch (scope.mode) {
    case "one":
      return [scope.competitionId];
    case "except":
      return uniq(seasonCompetitionIds).filter((id) => id !== scope.competitionId);
    case "all":
      return uniq(seasonCompetitionIds);
  }
}

/**
 * The scope a column falls back to when the user hasn't picked one. Preserves the
 * pre-C2 behaviour: show the ref's single (anchor) competition. But if the season
 * list is loaded and that competition ISN'T in it (e.g. the user just switched to
 * a season the anchor comp doesn't belong to), fall back to `all` rather than
 * querying a competition the player didn't play that season. While the list is
 * still loading (empty) we trust the anchor, so the first paint matches today's
 * single-competition numbers with no loading flicker.
 */
export function defaultCompetitionScope(
  refCompetitionId: number | null,
  seasonCompetitionIds: number[]
): CompetitionScope {
  if (
    refCompetitionId !== null &&
    (seasonCompetitionIds.length === 0 || seasonCompetitionIds.includes(refCompetitionId))
  ) {
    return { mode: "one", competitionId: refCompetitionId };
  }
  return { mode: "all" };
}
